import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { makeStyles } from '@coreai/fluentui-react';
import { PreservedNavigate } from './components/PreservedNavigate';
import { QueryParamPreserver } from './components/QueryParamPreserver';
import { StagingBanner } from './components/StagingBanner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ConnectionsPage } from './pages/ConnectionsPage';
import { ParametersPage } from './pages/ParametersPage';
import { SandboxConfigsPage } from './pages/SandboxConfigsPage';
import { KnowledgeSourcesPage } from './pages/KnowledgeSourcesPage';
import { WorkflowDesignerPage } from './pages/WorkflowDesignerPage';
import { LoginPage } from './pages/LoginPage';
import { OAuthCallbackPage } from './pages/OAuthCallbackPage';
import { GitHubCallbackPage } from './pages/GitHubCallbackPage';
import { AuthRedirectPage } from './pages/AuthRedirectPage';
import { useRouteTracking } from './hooks/useTelemetry';
import { isFeatureEnabled } from './featureFlags';

// Dashboard components
import { RootLayout } from './components/layouts/RootLayout';
import { TenantLayout } from './components/layouts/TenantLayout';
import { HomeLayout } from './components/layouts/HomeLayout';
import { ProjectLayout } from './components/layouts/ProjectLayout';
import { AppLayout } from './components/layouts/AppLayout';
import { AppListPage } from './pages/AppListPage';
import { WorkflowListPage } from './pages/WorkflowListPage';
import { ProjectSettingsPage } from './pages/ProjectSettingsPage';
import { AppSettingsPage } from './pages/AppSettingsPage';

import { DashboardHomePage } from './pages/DashboardHomePage';

// Lazy-loaded pages — split into separate chunks to reduce initial bundle
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const AgentsPage = lazy(() => import('./pages/AgentsPage').then(m => ({ default: m.AgentsPage })));
const AgentDetailPage = lazy(() => import('./pages/AgentDetailPage').then(m => ({ default: m.AgentDetailPage })));

const RouteTracker = () => {
  useRouteTracking();
  return null;
};

// App shell: column flex so the StagingBanner takes its own row above the
// routed layout. Without this the banner would either overlay the sticky
// header (position: fixed) or be ignored by the layout's height: 100%.
const useShellStyles = makeStyles({
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  content: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
});

/** Shared app-level routes — reused under both bare and tenant-prefixed paths */
const AppRoutes = () => (
  <>
    <Route index element={<PreservedNavigate to="workflows" replace />} />
    <Route path="workflows" element={<WorkflowListPage />} />
    <Route path="workflows/:workflowName" element={<WorkflowDesignerPage />} />
    <Route path="workflows/:workflowName/runs" element={<WorkflowDesignerPage />} />
    <Route path="workflows/:workflowName/runs/:runId" element={<WorkflowDesignerPage />} />
    <Route path="connections" element={<ConnectionsPage />} />
    <Route path="parameters" element={<ParametersPage />} />
    <Route path="analytics" element={<Suspense fallback={null}><AnalyticsPage /></Suspense>} />
    <Route path="settings" element={<AppSettingsPage />} />
    <Route path="agents" element={<Suspense fallback={null}><AgentsPage /></Suspense>} />
    <Route path="agents/:agentName" element={<Suspense fallback={null}><AgentDetailPage /></Suspense>} />
  </>
);

/** Shared project routes: /projects/:projectName → auto-redirect to /apps, then app tree */
const ProjectRoutes = () => (
  <>
    <Route path=":projectName" element={<ProjectLayout />}>
      <Route index element={<PreservedNavigate to="apps" replace />} />
      <Route path="settings" element={<ProjectSettingsPage />} />
      {isFeatureEnabled('knowledgeManagement') && <Route path="knowledge" element={<KnowledgeSourcesPage />} />}
      <Route path="sandboxes" element={<SandboxConfigsPage />} />
      <Route path="apps" element={<AppListPage />} />
      <Route path="apps/:appName" element={<AppLayout />}>
        {AppRoutes()}
      </Route>
    </Route>
  </>
);

const AppContent = () => {
  const shellStyles = useShellStyles();
  return (
    <>
      <RouteTracker />
      <QueryParamPreserver />
      <div className={shellStyles.shell}>
        <StagingBanner />
        <div className={shellStyles.content}>
          <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/redirect.html" element={<AuthRedirectPage />} />
        <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
        <Route path="/github/callback" element={<GitHubCallbackPage />} />

        {/* ═══ Dashboard home (no tenant, no location) ═══ */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <RootLayout />
            </ProtectedRoute>
          }
        >
          <Route element={<HomeLayout />}>
            <Route index element={<DashboardHomePage />} />
          </Route>
        </Route>

        {/* ═══ Tenant-scoped dashboard (no location) ═══ */}
        <Route
          path="/tenant/:tenantId"
          element={
            <ProtectedRoute>
              <TenantLayout />
            </ProtectedRoute>
          }
        >
          <Route element={<RootLayout />}>
            <Route element={<HomeLayout />}>
              <Route index element={<DashboardHomePage />} />
            </Route>
          </Route>
        </Route>

        {/* ═══ Location-scoped (no tenant): /location/{region}/... ═══ */}
        <Route
          path="/location/:locationName"
          element={
            <ProtectedRoute>
              <RootLayout />
            </ProtectedRoute>
          }
        >
          {/* Bare /location/{region} → project list for that region */}
          <Route element={<HomeLayout />}>
            <Route index element={<DashboardHomePage />} />
          </Route>
          {/* /location/{region}/projects/{name}/... */}
          <Route path="projects">
            {ProjectRoutes()}
          </Route>
        </Route>

        {/* ═══ Tenant + location: /tenant/{guid}/location/{region}/... ═══ */}
        <Route
          path="/tenant/:tenantId/location/:locationName"
          element={
            <ProtectedRoute>
              <TenantLayout />
            </ProtectedRoute>
          }
        >
          <Route element={<RootLayout />}>
            <Route element={<HomeLayout />}>
              <Route index element={<DashboardHomePage />} />
            </Route>
            <Route path="projects">
              {ProjectRoutes()}
            </Route>
          </Route>
        </Route>

        {/* ═══ Catch-all: unknown URLs → dashboard home ═══ */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <PreservedNavigate to="/" replace />
            </ProtectedRoute>
          }
        />
          </Routes>
        </div>
      </div>
    </>
  );
};

export const App = () => (
  <BrowserRouter>
    <AppContent />
  </BrowserRouter>
);
